import { Gavel, History, Info, ListChecks, Paperclip, Plus, RefreshCw } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import {
  api,
  isApiError,
  type AbuseCaseDetail,
  type AbuseCaseStatus,
  type AbuseTransitionRequest,
  type DomainActionKind,
  type DomainActionRequest,
  type LawEnforcementRequest,
  type LawEnforcementStatus,
  type LawEnforcementUpdate,
  type NewEvidence,
  type NewLawEnforcementRequest,
} from '@/api';
import { Badge } from '@/components/Badge';
import { Callout, type Column, DataTable, Field, Section } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { EntityRef } from '@/components/EntityRef';
import { CorrelationLink, PlainId, RegistryTxLink } from '@/components/Ids';
import { KeyValue } from '@/components/KeyValue';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { EmptyState, Loading } from '@/components/States';
import { useRetained, useWrite, WriteError } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { formatDateTime, formatRelative } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { ActionDialog, ConfirmCheck, DueCell, EnumBadge, FormActions, FormGrid, MfaHint, SelectField, Timeline, useEnumLabel, useEnumOptions } from './ui';
import { ABUSE_STATUS, ABUSE_TRANSITIONS, DOMAIN_ACTION_STATUS, LE_STATUS, LE_STATUSES, PRIORITY } from './vocab';
import { policyLabel } from '@/lib/labels';

type Dialogs = 'evidence' | 'action' | 'le' | null;
const TABS = ['summary', 'evidence', 'actions', 'history'] as const;
type Tab = (typeof TABS)[number];
const ACTIONS: DomainActionKind[] = ['SUSPEND', 'UNSUSPEND', 'LOCK', 'UNLOCK', 'NOTE_ONLY'];

function TransitionForm({ c, onDone }: { c: AbuseCaseDetail; onDone: () => void }) {
  const { t } = useTranslation('compliance');
  const options = useEnumOptions();
  const next = ABUSE_TRANSITIONS[c.status];
  const [to, setTo] = useState<AbuseCaseStatus | ''>(next[0] ?? '');
  const [note, setNote] = useState('');
  const [resolution, setResolution] = useState(c.resolution ?? '');
  const write = useWrite((req: AbuseTransitionRequest, opts) => api.transitionAbuseCase(c.id, req, opts));
  const resolving = to === 'RESOLVED' || to === 'CLOSED';
  const needsResolution = resolving && !c.resolution;
  const valid = to !== '' && (!needsResolution || resolution.trim() !== '');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    const res = await write.run({
      to,
      version: c.version,
      ...(note.trim() ? { note: note.trim() } : {}),
      ...(resolution.trim() && resolving ? { resolution: resolution.trim() } : {}),
    });
    if (res) onDone();
  };

  if (next.length === 0) return <p className="text-sm text-muted-foreground">{t('abuse.detail.noTransitions')}</p>;

  return (
    <form onSubmit={submit} className="grid gap-4">
      <FormGrid>
        <SelectField label={t('abuse.transition.newStatus')} value={to} options={options('abuseStatus', next)} onChange={setTo} disabled={write.busy} />
        <Field label={t('abuse.transition.note')}>
          <Input value={note} onChange={(e) => setNote(e.target.value)} disabled={write.busy} maxLength={1000} />
        </Field>
        {resolving ? (
          <Field label={needsResolution ? t('abuse.transition.resolutionRequired') : t('abuse.transition.resolutionReplace')} className="sm:col-span-2">
            <Textarea rows={2} value={resolution} onChange={(e) => setResolution(e.target.value)} disabled={write.busy} required={needsResolution} maxLength={4000} />
          </Field>
        ) : null}
      </FormGrid>
      <p className="text-xs text-muted-foreground">{t('abuse.transition.version', { version: c.version })}</p>
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <div>
        <Button type="submit" disabled={write.busy || !valid}>
          {write.busy ? t('common.saving') : t('abuse.transition.submit')}
        </Button>
      </div>
    </form>
  );
}

function EvidenceForm({ c, onDone, onCancel }: { c: AbuseCaseDetail; onDone: () => void; onCancel: () => void }) {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const [url, setUrl] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [sha, setSha] = useState('');
  const [description, setDescription] = useState('');
  const [captured, setCaptured] = useState('');
  const write = useWrite((req: NewEvidence, opts) => api.addAbuseEvidence(c.id, req, opts));
  const shaOk = /^[0-9a-f]{64}$/.test(sha.trim().toLowerCase());
  const valid = shaOk && description.trim() !== '' && (url.trim() !== '' || documentId.trim() !== '');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    const res = await write.run({
      url: url.trim() || null,
      document_id: documentId.trim() || null,
      sha256: sha.trim().toLowerCase(),
      description: description.trim(),
      captured_at: captured ? new Date(`${captured}:00Z`).toISOString() : null,
    });
    if (res) onDone();
  };

  return (
    <form onSubmit={submit} className="grid gap-4">
      <FormGrid>
        <Field label={t('abuse.evidence.url')} className="sm:col-span-2">
          <Input type="url" value={url} onChange={(e) => setUrl(e.target.value)} disabled={write.busy} placeholder="https://…" />
        </Field>
        <Field label={`${t('abuse.evidence.or')} ${t('abuse.evidence.documentId')}`}>
          <Input value={documentId} onChange={(e) => setDocumentId(e.target.value)} disabled={write.busy} className="font-mono" />
        </Field>
        <Field label={t('abuse.evidence.captured')}>
          <Input type="datetime-local" value={captured} onChange={(e) => setCaptured(e.target.value)} disabled={write.busy} />
        </Field>
        <Field label={t('abuse.evidence.sha')} className="sm:col-span-2" hint={sha && !shaOk ? <span className="text-critical">{t('abuse.evidence.shaInvalid')}</span> : undefined}>
          <Input value={sha} onChange={(e) => setSha(e.target.value)} disabled={write.busy} className="font-mono" spellCheck={false} aria-invalid={Boolean(sha) && !shaOk} />
        </Field>
        <Field label={t('abuse.evidence.description_')} className="sm:col-span-2">
          <Input value={description} onChange={(e) => setDescription(e.target.value)} disabled={write.busy} />
        </Field>
      </FormGrid>
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy || !valid}>
          {write.busy ? t('abuse.evidence.busy') : t('abuse.evidence.submit')}
        </Button>
      </FormActions>
    </form>
  );
}

function DomainActionForm({ c, onDone, onCancel }: { c: AbuseCaseDetail; onDone: () => void; onCancel: () => void }) {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  const options = useEnumOptions();
  const [action, setAction] = useState<DomainActionKind>('SUSPEND');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const write = useWrite((req: DomainActionRequest, opts) => api.requestDomainAction(c.id, req, opts));
  const registry = action === 'SUSPEND' || action === 'UNSUSPEND';
  const valid = reason.trim() !== '' && confirm;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    const res = await write.run({ action, reason: reason.trim() });
    if (res) onDone();
  };

  return (
    <form onSubmit={submit} className="grid gap-4">
      <SelectField
        label={t('abuse.action.action')}
        value={action}
        options={options('domainAction', ACTIONS)}
        onChange={(v) => {
          if (!v) return;
          setAction(v);
          setConfirm(false);
        }}
        disabled={write.busy}
        hint={t(`abuse.action.help.${action}`)}
      />
      <Field label={t('abuse.action.reason')}>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} disabled={write.busy} maxLength={2000} />
      </Field>
      <ConfirmCheck checked={confirm} onChange={setConfirm} disabled={write.busy}>
        {registry ? t('abuse.action.confirm', { domain: c.reported_name }) : t('abuse.action.confirmLocal', { domain: c.reported_name })}
      </ConfirmCheck>
      <p className="text-xs text-muted-foreground">{t('abuse.action.idempotent')}</p>
      <MfaHint />
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" variant={registry ? 'destructive' : 'default'} disabled={write.busy || !valid}>
          {write.busy
            ? t('abuse.action.busy')
            : t('abuse.action.submit', {
                action: label('domainAction', action),
              })}
        </Button>
      </FormActions>
    </form>
  );
}

function LawEnforcementForm({ c, onDone, onCancel }: { c: AbuseCaseDetail; onDone: () => void; onCancel: () => void }) {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const [f, setF] = useState({
    agency: '',
    jurisdiction: 'VN',
    reference: '',
    legal_basis: '',
    deadline: '',
  });
  const write = useWrite((req: NewLawEnforcementRequest, opts) => api.addLawEnforcementRequest(c.id, req, opts));
  const jurisdictionOk = /^[A-Z]{2}(-[A-Z0-9]{1,3})?$/.test(f.jurisdiction);
  const valid = f.agency.trim() && f.reference.trim() && f.legal_basis.trim() && jurisdictionOk;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    const res = await write.run({
      agency: f.agency.trim(),
      jurisdiction: f.jurisdiction,
      reference: f.reference.trim(),
      legal_basis: f.legal_basis.trim(),
      deadline_at: f.deadline ? new Date(`${f.deadline}:00Z`).toISOString() : null,
    });
    if (res) onDone();
  };

  return (
    <form onSubmit={submit} className="grid gap-4">
      <FormGrid>
        <Field label={t('abuse.leForm.agency')}>
          <Input value={f.agency} onChange={(e) => setF({ ...f, agency: e.target.value })} disabled={write.busy} />
        </Field>
        <Field label={t('abuse.leForm.jurisdiction')}>
          <Input value={f.jurisdiction} onChange={(e) => setF({ ...f, jurisdiction: e.target.value.toUpperCase() })} disabled={write.busy} aria-invalid={!jurisdictionOk} />
        </Field>
        <Field label={t('abuse.leForm.reference')}>
          <Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} disabled={write.busy} />
        </Field>
        <Field label={t('abuse.leForm.deadline')}>
          <Input type="datetime-local" value={f.deadline} onChange={(e) => setF({ ...f, deadline: e.target.value })} disabled={write.busy} />
        </Field>
        <Field label={t('abuse.leForm.legalBasis')} className="sm:col-span-2">
          <Input value={f.legal_basis} onChange={(e) => setF({ ...f, legal_basis: e.target.value })} disabled={write.busy} />
        </Field>
      </FormGrid>
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy || !valid}>
          {write.busy ? t('abuse.leForm.busy') : t('abuse.leForm.submit')}
        </Button>
      </FormActions>
    </form>
  );
}

function LeUpdate({ r, onDone }: { r: LawEnforcementRequest; onDone: () => void }) {
  const { t } = useTranslation('compliance');
  const options = useEnumOptions();
  const [status, setStatus] = useState<LawEnforcementStatus>(r.status);
  const [summary, setSummary] = useState(r.response_summary ?? '');
  const write = useWrite((req: LawEnforcementUpdate, opts) => api.updateLawEnforcementRequest(r.id, req, opts));
  const needsSummary = (status === 'RESPONDED' || status === 'REJECTED') && !summary.trim();
  return (
    <form
      className="mt-3 grid gap-3 sm:grid-cols-[12rem_1fr_auto] sm:items-end"
      onSubmit={async (e) => {
        e.preventDefault();
        if (await write.run({ status, response_summary: summary.trim() || null })) onDone();
      }}
    >
      <SelectField label={t('abuse.leForm.newStatus')} value={status} options={options('leStatus', LE_STATUSES)} onChange={(v) => v && setStatus(v)} disabled={write.busy} />
      <Field label={t('abuse.leForm.responseSummary')}>
        <Input value={summary} onChange={(e) => setSummary(e.target.value)} disabled={write.busy} />
      </Field>
      <Button type="submit" variant="outline" disabled={write.busy || status === r.status || needsSummary}>
        {t('abuse.leForm.update')}
      </Button>
      {write.state.phase === 'error' ? (
        <div className="sm:col-span-3">
          <WriteError error={write.state.error} />
        </div>
      ) : null}
    </form>
  );
}

type Evidence = AbuseCaseDetail['evidence'][number];
type DomainAction = AbuseCaseDetail['domain_actions'][number];

/** `/compliance/icann/abuse/:id`: one abuse case with its reports, evidence, domain actions and LE requests. */
export function AbuseCasePage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const result = useApi((signal) => api.getAbuseCase(id, { signal }), [id]);
  const c = useRetained(result.status === 'success' ? result.data : undefined);
  const [dialog, setDialog] = useState<Dialogs>(null);
  const rawTab = params.get('tab');
  const tab: Tab = (TABS as readonly string[]).includes(rawTab ?? '') ? (rawTab as Tab) : 'summary';
  useDocumentTitle(c ? c.case_number : t('abuse.detail.title'));
  const crumbs = [
    { label: t('crumbs.compliance') },
    { label: t('crumbs.icann') },
    { label: t('abuse.list.title'), to: '/compliance/icann/abuse' },
    { label: c?.case_number ?? '…' },
  ];

  if (!c) {
    return (
      <>
        <PageHeader title={t('abuse.detail.title')} crumbs={crumbs} />
        {result.status === 'error' ? (
          <DataError error={result.error} onRetry={isApiError(result.error) && result.error.status === 404 ? undefined : result.reload} title={t('abuse.detail.loadError')} />
        ) : (
          <Loading rows={4} />
        )}
      </>
    );
  }

  const done = () => {
    setDialog(null);
    result.reload();
  };
  const setTab = (next: string) => {
    const p = new URLSearchParams(params);
    if (next === 'summary') p.delete('tab');
    else p.set('tab', next);
    setParams(p, { replace: true });
  };
  const closed = c.status === 'CLOSED';
  const overdue = Boolean(c.overdue) && !c.first_responded_at;

  const evidenceCols: Column<Evidence>[] = [
    {
      key: 'captured',
      header: t('abuse.detail.col.captured'),
      cell: (e) => <span className="whitespace-nowrap">{formatDateTime(e.captured_at)}</span>,
    },
    {
      key: 'description',
      header: t('abuse.detail.col.description'),
      cell: (e) => e.description,
    },
    {
      key: 'source',
      header: t('abuse.detail.col.source'),
      cell: (e) => (e.url ? <code className="text-xs break-all">{e.url}</code> : <PlainId id={e.document_id} />),
    },
    {
      key: 'sha',
      header: t('abuse.detail.col.sha'),
      cell: (e) => (
        <code className="text-xs" title={e.sha256}>
          {e.sha256.slice(0, 12)}…
        </code>
      ),
    },
  ];
  // A reversal points at another action on the same case, so name it by what it did and when.
  const reversedName = (actionId: string): string => {
    const target = c.domain_actions.find((x) => x.id === actionId);
    return target ? `${label('domainAction', target.action)} · ${formatDateTime(target.requested_at)}` : t('common.none');
  };
  const actionCols: Column<DomainAction>[] = [
    {
      key: 'requested',
      header: t('abuse.detail.col.requested'),
      cell: (a) => <span className="whitespace-nowrap">{formatDateTime(a.requested_at)}</span>,
    },
    {
      key: 'action',
      header: t('abuse.detail.col.action'),
      cell: (a) => (
        <>
          <span title={a.action} className="font-medium">
            {label('domainAction', a.action)}
          </span>
          {a.reverses_action_id ? (
            <div className="text-xs text-muted-foreground">
              {t('abuse.detail.reverses', { action: reversedName(a.reverses_action_id) })}
            </div>
          ) : null}
        </>
      ),
    },
    {
      key: 'status',
      header: t('abuse.detail.col.status'),
      cell: (a) => <EnumBadge group="domainActionStatus" value={a.status} tones={DOMAIN_ACTION_STATUS} />,
    },
    {
      key: 'reason',
      header: t('abuse.detail.col.reason'),
      cell: (a) => <span className="text-sm">{a.reason}</span>,
    },
    {
      key: 'registry',
      header: t('abuse.detail.col.registry'),
      cell: (a) => (
        <div className="text-sm">
          {a.registry_transaction_id ? <RegistryTxLink id={a.registry_transaction_id} /> : <span className="text-muted-foreground">{t('abuse.detail.noRegistryCommand')}</span>}
          {a.result_code !== null ? <div className="text-xs text-muted-foreground">{t('abuse.detail.result', { code: a.result_code })}</div> : null}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={<span className="font-mono">{c.case_number}</span>}
        crumbs={crumbs}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <EnumBadge group="abuseStatus" value={c.status} tones={ABUSE_STATUS} />
            <EnumBadge group="priority" value={c.priority} tones={PRIORITY} />
            <span title={c.category}>{label('abuseCategory', c.category)}</span>
            <span aria-hidden="true">·</span>
            {c.domain_id ? (
              <EntityRef label={c.reported_name} to={`/domains/${encodeURIComponent(c.reported_name)}`} id={c.domain_id} />
            ) : (
              <span title={t('common.notManaged')}>{c.reported_name}</span>
            )}
          </span>
        }
        actions={
          <Button size="sm" variant="outline" onClick={result.reload}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />
      <div className="space-y-4">
        {params.get('attached') === '1' ? <Callout tone="info">{t('abuse.detail.attached')}</Callout> : null}
        {overdue && c.first_response_due_at ? (
          <Callout tone="crit" title={t('sla.overdue')}>
            {t('abuse.detail.overdue', {
              when: formatRelative(c.first_response_due_at),
            })}
          </Callout>
        ) : null}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-auto flex-wrap">
            <TabsTrigger value="summary">
              <Info /> {t('common.tabs.summary')}
            </TabsTrigger>
            <TabsTrigger value="evidence">
              <Paperclip /> {t('common.tabs.evidence')} <Badge className="ml-1 px-1.5 py-0">{c.evidence.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="actions">
              <Gavel /> {t('common.tabs.actions')}
            </TabsTrigger>
            <TabsTrigger value="history">
              <History /> {t('common.tabs.history')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="summary" className="mt-2 space-y-4">
            <Section title={t('abuse.detail.facts')}>
              <KeyValue
                columns={3}
                rows={[
                  [t('abuse.detail.source'), label('abuseSource', c.source)],
                  [t('abuse.detail.opened'), `${formatDateTime(c.opened_at)} (${formatRelative(c.opened_at)})`],
                  [
                    t('abuse.detail.firstResponse'),
                    c.first_responded_at ? (
                      t('abuse.detail.respondedAt', {
                        at: formatDateTime(c.first_responded_at),
                      })
                    ) : (
                      <DueCell key="due" dueAt={c.first_response_due_at} overdue={overdue} policyMissing={c.sla_status === 'POLICY_MISSING'} />
                    ),
                  ],
                  [
                    t('abuse.detail.slaPolicy'),
                    c.sla_policy_key ? (
                      <EntityRef
                        key="sla"
                        label={policyLabel(c.sla_policy_key)}
                        to={`/compliance/policies/${encodeURIComponent(c.sla_policy_key)}`}
                        sub={c.sla_policy_version ?? undefined}
                      />
                    ) : (
                      '—'
                    ),
                  ],
                  [t('abuse.detail.assignedTo'), <EntityRef key="a" label={c.assigned_to_display_name} id={c.assigned_to} />],
                  [t('abuse.detail.statusChanged'), formatDateTime(c.status_changed_at)],
                  [t('abuse.detail.resolution'), c.resolution ?? '—'],
                  [t('abuse.detail.version'), c.version],
                ]}
              />
            </Section>
            <Section title={t('abuse.detail.reports', { count: c.reports.length })} flush>
              {c.reports.length === 0 ? (
                <div className="px-5 pb-5">
                  <EmptyState title={t('abuse.detail.noReports')} />
                </div>
              ) : (
                <ul className="divide-y border-t">
                  {c.reports.map((r) => (
                    <li key={r.id} className="space-y-1 px-5 py-3">
                      <p className="text-sm font-medium">{r.summary}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDateTime(r.received_at)} · {label('channel', r.channel)} · {label('reporterType', r.reporter_type)}
                        {r.has_reporter_contact ? ` · ${t('abuse.detail.contactOnFile')}` : ''}
                        {r.external_reference ? ` · ${t('abuse.detail.ref', { ref: r.external_reference })}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </TabsContent>

          <TabsContent value="evidence" className="mt-2">
            <Section
              title={t('abuse.detail.evidence', { count: c.evidence.length })}
              description={t('abuse.detail.evidenceHint')}
              actions={
                <Button size="sm" onClick={() => setDialog('evidence')} disabled={closed}>
                  <Plus /> {t('abuse.detail.addEvidence')}
                </Button>
              }
              flush
            >
              <DataTable columns={evidenceCols} rows={c.evidence} rowKey={(e) => e.id} empty={<EmptyState title={t('abuse.detail.noEvidence')} />} />
            </Section>
          </TabsContent>

          <TabsContent value="actions" className="mt-2 space-y-4">
            {closed ? <Callout tone="neutral">{t('abuse.detail.closedHint')}</Callout> : null}
            <Section title={t('abuse.detail.changeStatus')} description={t('abuse.detail.statusHint')}>
              <TransitionForm key={c.version} c={c} onDone={done} />
            </Section>
            <Section
              title={t('abuse.detail.actions', {
                count: c.domain_actions.length,
              })}
              description={c.domain_id ? t('abuse.detail.actionsHint') : t('abuse.detail.noDomain')}
              actions={
                <Button size="sm" variant="outline" onClick={() => setDialog('action')} disabled={!c.domain_id || closed}>
                  <ListChecks /> {t('abuse.detail.requestAction')}
                </Button>
              }
              flush
            >
              <DataTable columns={actionCols} rows={c.domain_actions} rowKey={(a) => a.id} empty={<EmptyState title={t('abuse.detail.noActions')} />} />
            </Section>
            <Section
              title={t('abuse.detail.le', {
                count: c.law_enforcement_requests.length,
              })}
              description={t('abuse.detail.leHint')}
              actions={
                <Button size="sm" variant="outline" onClick={() => setDialog('le')} disabled={closed}>
                  <Plus /> {t('abuse.detail.recordLe')}
                </Button>
              }
              flush
            >
              {c.law_enforcement_requests.length === 0 ? (
                <div className="px-5 pb-5">
                  <EmptyState title={t('abuse.detail.noLe')} />
                </div>
              ) : (
                <ul className="divide-y border-t">
                  {c.law_enforcement_requests.map((r) => (
                    <li key={r.id} className="px-5 py-3">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <EntityRef label={r.agency} to={`/compliance/law-enforcement-requests/${encodeURIComponent(r.id)}`} id={r.id} sub={r.reference} />
                        <EnumBadge group="leStatus" value={r.status} tones={LE_STATUS} />
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t('abuse.detail.leMeta', {
                          jurisdiction: r.jurisdiction,
                          basis: r.legal_basis,
                          at: formatDateTime(r.received_at),
                        })}
                        {r.deadline_at ? ` · ${t('abuse.detail.leDeadline', { at: formatDateTime(r.deadline_at), rel: formatRelative(r.deadline_at) })}` : ''}
                      </p>
                      {r.response_summary ? (
                        <p className="mt-1 text-sm">
                          {t('abuse.detail.leResponse', {
                            text: r.response_summary,
                          })}
                        </p>
                      ) : null}
                      {r.status !== 'CLOSED' ? <LeUpdate r={r} onDone={result.reload} /> : null}
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </TabsContent>

          <TabsContent value="history" className="mt-2">
            <Section title={t('abuse.detail.history')}>
              {c.actions.length === 0 ? (
                <EmptyState title={t('abuse.detail.noHistory')} />
              ) : (
                <Timeline
                  items={[...c.actions].reverse().map((a) => {
                    const group = a.action.startsWith('LAW_ENFORCEMENT') ? 'leStatus' : 'abuseStatus';
                    return {
                      key: a.id,
                      at: a.created_at,
                      title: (
                        <>
                          <span className="font-medium" title={a.action}>
                            {label('caseAction', a.action)}
                          </span>
                          {a.from_status || a.to_status ? (
                            <span className="text-muted-foreground">
                              {t('abuse.detail.transition', {
                                from: a.from_status ? label(group, a.from_status) : '∅',
                                to: a.to_status ? label(group, a.to_status) : '∅',
                              })}
                            </span>
                          ) : null}
                        </>
                      ),
                      meta: (
                        <>
                          <EntityRef label={c.actor_display_names[a.actor_id] ?? label('actorType', a.actor_type)} id={a.actor_id} />
                          {a.correlation_id ? <CorrelationLink id={a.correlation_id} label={t('common.correlation')} /> : null}
                        </>
                      ),
                    };
                  })}
                />
              )}
            </Section>
          </TabsContent>
        </Tabs>
      </div>

      <ActionDialog
        open={dialog === 'evidence'}
        onOpenChange={(o) => setDialog(o ? 'evidence' : null)}
        title={t('abuse.evidence.title')}
        description={t('abuse.evidence.description')}
        wide
      >
        <EvidenceForm c={c} onDone={done} onCancel={() => setDialog(null)} />
      </ActionDialog>
      <ActionDialog
        open={dialog === 'action'}
        onOpenChange={(o) => setDialog(o ? 'action' : null)}
        title={t('abuse.action.title', { domain: c.reported_name })}
        description={t('abuse.action.description')}
      >
        <DomainActionForm c={c} onDone={done} onCancel={() => setDialog(null)} />
      </ActionDialog>
      <ActionDialog open={dialog === 'le'} onOpenChange={(o) => setDialog(o ? 'le' : null)} title={t('abuse.leForm.title')} description={t('abuse.leForm.description')} wide>
        <LawEnforcementForm c={c} onDone={done} onCancel={() => setDialog(null)} />
      </ActionDialog>
    </>
  );
}
